import React from "react";
import { Icon } from "semantic-ui-react";

import { DetailsHeader } from "../../../../../components/sectionHeader";
import { PackStatusActionLabel } from "../../PackStatusActionLabel/PackStatusActionLabel";
import DateLabel from "./DateLabel";
import dateTimeUtils from "../../../../../utils/dateTimeUtils";
import { isBrainStorm } from "utils/accountUtils";
import Logo from "../../../../../images/brainstorm-pack-logo.png";
import RemovePackButton from "features/Licensing/Packs/Listings/removePackButton/RemovePackButton";
import { PackFreeLabel } from "components/packs/packsList/packFreeLabel/PackFreeLabel";
import { isFreePricingType, isOtherPricingType } from "../../utils/packUtils";

import "./listingOverviewHeader.scss";

export interface Props {
  title: string;
  titleForGA?: string;
  backButton: string | boolean;
  defaultURL: string;
  isRequested: boolean;
  isPurchased: boolean;
  isExpired: boolean;
  isArchived: boolean;
  isTrial: boolean;
  isFromTemplate: boolean;
  isPrivate: boolean;
  trialPeriodName?: string;
  expirationDate?: string;
  isOwn: boolean;
  isTrialAllowed: boolean;
  publisher?: string;
  logoUri?: string;
  onPackRequest: () => void;
  onPackRemove: () => void;
  onPackTrialRequest: () => void;
  onEditClick: () => void;
  renderActionButtons?: () => React.ReactElement[];
  hasPurchasePackAddon: boolean;
  packPricingTypeId: number;
}

export const ListingOverviewHeader: React.FC<Props> = ({
  title,
  titleForGA,
  backButton,
  defaultURL,
  isOwn,
  isRequested,
  isPurchased,
  isExpired,
  isArchived,
  isTrial,
  isFromTemplate,
  isPrivate,
  trialPeriodName,
  expirationDate,
  onPackRequest,
  onPackRemove,
  onPackTrialRequest,
  onEditClick,
  renderActionButtons,
  isTrialAllowed,
  publisher,
  hasPurchasePackAddon,
  packPricingTypeId,
}) => {
  const isFree = isFreePricingType(packPricingTypeId);
  const isOther = isOtherPricingType(packPricingTypeId);
  const isRequestButtonVisible = !(isOwn || isPurchased || isExpired);
  const isRemoveButtonVisible = !isOwn && isPurchased && isFree && !isExpired;
  const isPurchasedLabelVisible = !isOwn && isPurchased && !isExpired && !isTrial && !isFree && !isOther;
  const isExpiredLabelVisible = isPurchased && isExpired && !isTrial && !isOther;
  const isTrialExpiredLabelVisible = isTrial && isExpired;
  const isTrialStartedLabelVisible = isTrial && !isExpired;
  const licenseExpirationDate = expirationDate
    ? dateTimeUtils.toLocalFormatDateTime(new Date(expirationDate), "MM/DD/YYYY")
    : "";

  return (
    <>
      <DetailsHeader
        title={title}
        titleForGA={titleForGA}
        backButton={backButton}
        defaultURL={defaultURL}
        canBeEdited={isOwn}
        isRevertVisible={false}
        isReadOnly={!isOwn}
        showReadOnlyLabel={false}
        invalidFormDetails={false}
        canBePublished={false}
        renderEditButton
        entityStateActions={{
          onPublish: /* istanbul ignore next */ () => {}, // NOSONAR
          onClose: /* istanbul ignore next */ () => {}, // NOSONAR
          onEdit: () => onEditClick(),
          onRevert: /* istanbul ignore next */ () => {}, // NOSONAR
        }}
        actionButtons={isOwn ? renderActionButtons?.() : undefined}
        style={{ minHeight: 62 }}
      >
        {isRequestButtonVisible && (
          <PackStatusActionLabel
            isExpired={isExpired}
            isOwn={isOwn}
            isPurchased={isPurchased}
            isRequested={isRequested}
            isTrialAllowed={isTrialAllowed}
            trialPeriodName={trialPeriodName}
            onRequestClick={onPackRequest}
            onTrialClick={onPackTrialRequest}
            packPricingTypeId={packPricingTypeId}
            hasPurchasePackAddon={hasPurchasePackAddon}
            isArchived={isArchived}
            isFromTemplate={isFromTemplate}
            isPrivate={isPrivate}
            minimalStatus
          />
        )}
        {isRemoveButtonVisible && <RemovePackButton onClick={onPackRemove} disabled={isFromTemplate || isPrivate} />}
        {isPurchasedLabelVisible && (
          <span className="header-label header-label__purchased">
            <Icon name="checkmark" />
            <span className="label-with-date">
              <span className="status">Purchased</span>
              <DateLabel label="Expires" date={licenseExpirationDate} />
            </span>
          </span>
        )}
        {isExpiredLabelVisible && (
          <span className="header-label header-label__expired">
            <Icon name="exclamation triangle" />
            <span className="label-with-date">
              <span className="status">Expired</span>
              <DateLabel date={licenseExpirationDate} />
            </span>
          </span>
        )}
        {isTrialStartedLabelVisible && (
          <span className="header-label header-label__purchased">
            <Icon name="checkmark" />
            <span className="label-with-date">
              <span className="status">Trial Started</span>
              <DateLabel label="Expires" date={licenseExpirationDate} />
            </span>
          </span>
        )}
        {isTrialExpiredLabelVisible && (
          <span className="header-label header-label__expired">
            <Icon name="exclamation triangle" />
            <span className="label-with-date">
              <span className="status">Trial Ended</span>
              <DateLabel date={licenseExpirationDate} />
            </span>
          </span>
        )}
      </DetailsHeader>
      {publisher && (
        <div className="secondary-headline">
          <div className="vendor">
            {isBrainStorm(publisher) && (
              <div className="pack-publisher-logo">
                <img src={Logo} className="pack-publisher-logo-center" alt="BrainStorm Logo" />
              </div>
            )}
            By {publisher}
          </div>
          {isFree && (
            <>
              <div className="vertical-divider" />
              <PackFreeLabel isFree={isFree} />
            </>
          )}
        </div>
      )}
      <div className="divider" />
    </>
  );
};
